import { Box, Button, CircularProgress, MenuItem, Select, Tooltip, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useAuth } from '../../../../hooks/use-auth'
import { type SystemTimeSpan } from '../../../../models/SystemTimeSpan'
import { GetSystemConfigValues, GetSystemTimeSpans, UpdateSystemConfigValues } from '../../../../services/SystemConfigurationService'
import { SecurityLevel } from '../../../../utils/genericTypes'
import { DatePickerWithLabel } from '../../../customComponents/DatePickerWithLabel'
import { Modal } from '../../../customComponents/Modal'

const CONFIG_KEY_LABELS: Record<string, string> = {
  DefaultImplementationDueDate: 'Default Implementation Due Date',
  MidYearReviewDueDate: 'Mid-Year Review Due Date',
  CoachingCapacityToolDueDate: 'Coaching Capacity Tool Due Date',
  FocusStudentDataBOYDueDate: 'Focus Student Data (BOY) Due Date',
  FocusStudentDataEOYDueDate: 'Focus Student Data (EOY) Due Date',
  ProgressReportEOYDueDate: 'Progress Report (EOY) Due Date',
  PriorityOneInitialReviewDueDate: 'Priority One Initial Review Due Date',
  PriorityOneFinalReviewDueDate: 'Priority One Final Review Due Date'
}

const ALL_KEYS = Object.keys(CONFIG_KEY_LABELS)

function parseDateValue (value: string | undefined): Date | null {
  if (value == null || value === '') return null
  const parts = value.split('-').map(Number)
  if (parts.length !== 3 || parts.some(isNaN)) return null
  return new Date(parts[0], parts[1] - 1, parts[2])
}

function formatDateForApi (date: Date | null): string {
  if (date == null) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const DateConfigurations = (): JSX.Element => {
  const { user } = useAuth()
  const canEdit = user?.securityLevel === SecurityLevel.ProjectAdmin
  const [timeSpans, setTimeSpans] = useState<SystemTimeSpan[]>([])
  const [selectedTimeSpanId, setSelectedTimeSpanId] = useState<number | null>(null)
  const [configValues, setConfigValues] = useState<Record<string, Date | null>>({})
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  useEffect(() => {
    const fetchTimeSpans = async (): Promise<void> => {
      const { response, success } = await GetSystemTimeSpans()
      if (!success || response == null) {
        if (!success) toast.error('Failed to load school years')
        setLoadError(true)
        setLoading(false)
        return
      }

      setTimeSpans(response)

      const today = new Date()
      const current = response.find(
        ts => new Date(ts.startDate) <= today && new Date(ts.endDate) >= today
      )
      const defaultId = current?.id ?? response[0]?.id ?? null
      setSelectedTimeSpanId(defaultId)
    }

    void fetchTimeSpans()
  }, [])

  useEffect(() => {
    if (selectedTimeSpanId == null) return

    const fetchValues = async (): Promise<void> => {
      setLoading(true)
      const { response, success } = await GetSystemConfigValues(selectedTimeSpanId)
      const raw: Record<string, string> = (success && response != null) ? response : {}
      const parsed: Record<string, Date | null> = {}
      for (const key of ALL_KEYS) {
        parsed[key] = parseDateValue(raw[key])
      }
      setConfigValues(parsed)
      setLoading(false)
    }

    void fetchValues()
  }, [selectedTimeSpanId])

  const selectedTimeSpan = timeSpans.find(ts => ts.id === selectedTimeSpanId)
  const today = new Date()
  const isPastYear = selectedTimeSpan != null && new Date(selectedTimeSpan.endDate) < today
  const isFutureYear = selectedTimeSpan != null && new Date(selectedTimeSpan.startDate) > today

  const handleDateChange = (key: string, value: Date | null): void => {
    setConfigValues(prev => ({ ...prev, [key]: value }))
  }

  const hasAllDates = ALL_KEYS.every(key => configValues[key] != null)

  const handleSave = (): void => {
    setShowConfirm(true)
  }

  const handleConfirm = async (): Promise<void> => {
    if (selectedTimeSpanId == null) return

    const values: Record<string, string> = {}
    for (const key of ALL_KEYS) {
      const date = configValues[key]
      if (date != null) {
        values[key] = formatDateForApi(date)
      }
    }

    const { success } = await UpdateSystemConfigValues(selectedTimeSpanId, values)
    if (success) {
      toast.success('Date configurations saved')
    } else {
      toast.error('Failed to save date configurations')
    }
    setShowConfirm(false)
  }

  if (loadError) {
    return <Typography color='error'>Failed to load date configurations. Please try again later.</Typography>
  }

  if (timeSpans.length === 0) {
    return <CircularProgress />
  }

  return <>
    <Box sx={{ mt: '3em' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '1em', mb: '1.5em' }}>
        <Typography variant='h5' sx={{ fontSize: '1em', fontWeight: '600' }}>Date Configurations</Typography>
        <Select
          value={selectedTimeSpanId ?? ''}
          onChange={(e) => { setSelectedTimeSpanId(Number(e.target.value)) }}
          sx={{ fontSize: '.875em', height: '2em' }}
          inputProps={{ 'aria-label': 'School Year' }}
          data-testid='dateConfigYearSelect'
        >
          {timeSpans.map(ts => (
            <MenuItem key={ts.id} value={ts.id}>{ts.name}</MenuItem>
          ))}
        </Select>
        {isPastYear && (
          <Typography variant='body2' sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            Read-only (past year)
          </Typography>
        )}
      </Box>

      {loading
        ? <CircularProgress size={24} />
        : <>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5em', maxWidth: '800px' }}>
            {ALL_KEYS.map(key => {
              const isMissing = isFutureYear && configValues[key] == null
              return (
                <Tooltip
                  key={key}
                  title={isMissing ? 'Not yet configured for this year' : ''}
                  placement='top'
                >
                  <Box sx={{ border: isMissing ? '1px solid #f0ad4e' : 'none', borderRadius: '4px', p: isMissing ? '.5em' : 0 }}>
                    <DatePickerWithLabel
                      name={key}
                      label={CONFIG_KEY_LABELS[key]}
                      value={configValues[key] ?? null}
                      disabled={isPastYear || !canEdit}
                      dataTestId={`dateConfig-${key}`}
                      onChange={(date) => { handleDateChange(key, date) }}
                    />
                  </Box>
                </Tooltip>
              )
            })}
          </Box>

          {!isPastYear && canEdit && (
            <Box sx={{ mt: '1.5em' }}>
              <Button
                variant='contained'
                onClick={handleSave}
                disabled={!hasAllDates}
                data-testid='saveDateConfigurations'
              >
                Save Dates
              </Button>
              {!hasAllDates && (
                <Typography variant='body2' sx={{ color: 'error.main', mt: '.5em' }}>
                  All dates must be set before saving.
                </Typography>
              )}
            </Box>
          )}
        </>
      }
    </Box>

    {showConfirm && (
      <Modal
        open={true}
        title='Save Date Configurations'
        maxWidth='sm'
        cancelButtonText='Cancel'
        confirmButtonText='Save'
        onClose={() => { setShowConfirm(false) }}
        onConfirm={() => { void handleConfirm() }}
        bodyContent={
          <Box>
            <Typography variant='body1' sx={{ mb: '1em' }}>
              You are saving the following dates for <strong>{selectedTimeSpan?.name}</strong>:
            </Typography>
            {ALL_KEYS.map(key => (
              <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', mb: '.5em' }}>
                <Typography variant='body2' sx={{ fontWeight: 500 }}>{CONFIG_KEY_LABELS[key]}</Typography>
                <Typography variant='body2'>
                  {configValues[key] != null
                    ? configValues[key]?.toLocaleDateString()
                    : <em style={{ color: '#999' }}>Not set</em>
                  }
                </Typography>
              </Box>
            ))}
          </Box>
        }
      />
    )}
  </>
}
